<script setup lang="ts">
import type { NavigationMenuItem, DropdownMenuItem } from '@nuxt/ui'
import { useMediaQuery } from '@vueuse/core'

const { user, isAdmin, hasFeature, logout, hasTeamPlan } = useAuth()
const { isOpen: sidebarOpen } = useSidebar()
const route = useRoute()

// Открывает панель AI-чата (ChatFab.vue, смонтирована глобально в app.vue) — isOpen общий
// через useState, ChatFab сам реагирует на него (см. useChatFab.ts), независимо от того,
// что его переключило (тот же принцип, что в HeaderActions.vue). В отличие от кнопки в
// шапке (которая намеренно не трогает isExpanded — помнит последний режим), эта кнопка
// явно раскрывает чат в полноразмерном виде (сайдбар справа), как и просили.
const { isOpen: chatOpen, isExpanded: chatExpanded } = useChatFabState()
function openAiChat() {
  if (!chatOpen.value) chatExpanded.value = true
  chatOpen.value = !chatOpen.value
}
const hideAiButton = sidebarHidden().has('ai')

// Быстрое переключение темы — та же логика/state, что и на странице настроек
// (pages/settings.vue), просто ещё одна точка входа, под "Уведомлениями".
const { isDark, toggleTheme } = useTheme()

// Слайдовер уведомлений — открывается кнопкой "Уведомления" внизу сайдбара.
// Данные — таблица notifications (server/src/routes/notifications.ts): итоги закупок,
// напоминания о задачах, запросы КП, поздравления.
interface AppNotification {
  id: number
  title: string
  message: string | null
  notification_type: string | null
  entity_type: string | null
  entity_id: number | null
  is_read: boolean
  created_at: string
}
const notificationsOpen = ref(false)
const notifications = ref<AppNotification[]>([])
const notificationsUnread = ref(0)
const notificationsLoading = ref(false)

// Лимиты AI команды на месяц (блок под меню): обработано тендеров из лимита (150 по
// умолчанию) — GET /api/teams/ai-usage, тот же подсчёт, что в проверке квоты на сервере.
interface AiUsage {
  ai_enabled: boolean
  unlimited?: boolean
  tenders_used: number
  tenders_limit: number
  chat_used: number
  chat_limit: number
}
const aiUsage = ref<AiUsage | null>(null)
async function loadAiUsage() {
  try {
    aiUsage.value = await useApi<AiUsage>('/teams/ai-usage')
  } catch { /* silent */ }
}
const aiTendersPercent = computed(() => {
  const u = aiUsage.value
  if (!u?.tenders_limit) return 0
  return Math.min(100, Math.round((u.tenders_used / u.tenders_limit) * 100))
})

async function loadNotificationsUnread() {
  try {
    const data = await useApi<{ count: number }>('/notifications/unread-count')
    notificationsUnread.value = data.count
  } catch { /* silent */ }
}

async function loadNotifications() {
  notificationsLoading.value = true
  try {
    const data = await useApi<{ items: AppNotification[]; unread: number }>('/notifications?limit=50')
    notifications.value = data.items
    notificationsUnread.value = data.unread
  } catch { /* silent */ } finally {
    notificationsLoading.value = false
  }
}

watch(notificationsOpen, (open) => { if (open) loadNotifications() })

function notificationLink(n: AppNotification): string | null {
  if (n.entity_type === 'tender' && n.entity_id) return `/tenders/${n.entity_id}`
  if (n.entity_type === 'task' && n.entity_id) return `/tasks/${n.entity_id}`
  if (n.entity_type === 'kp_request') return '/commercial_proposal/requests'
  // settings_<вкладка> — напоминание включить Telegram (сотруднику) / сводка админу
  if (n.entity_type?.startsWith('settings_')) return `/settings?tab=${n.entity_type.slice('settings_'.length)}`
  return null
}

function notificationIcon(n: AppNotification): string {
  if (n.notification_type === 'task_reminder') return 'lucide:alarm-clock'
  if (n.notification_type === 'kp_request') return 'lucide:file-text'
  if (n.notification_type === 'birthday') return 'lucide:cake'
  if (n.notification_type === 'tender_calculated') return 'lucide:calculator'
  if (n.notification_type === 'telegram_setup') return 'lucide:send'
  if (n.title.includes('выиграна')) return 'lucide:trophy'
  if (n.title.includes('отменена')) return 'lucide:circle-x'
  return 'lucide:bell'
}

// Группы в слайдовере — по типу уведомления, в этом порядке; внутри группы свежие сверху
// (порядок с сервера). Неизвестный тип — в «Прочее».
const NOTIFICATION_GROUPS = [
  { type: 'result', label: 'Итоги закупок', icon: 'lucide:gavel' },
  { type: 'tender_calculated', label: 'Расчёты', icon: 'lucide:calculator' },
  { type: 'task_reminder', label: 'Задачи', icon: 'lucide:alarm-clock' },
  { type: 'kp_request', label: 'Запросы КП', icon: 'lucide:file-text' },
  { type: 'birthday', label: 'Дни рождения', icon: 'lucide:cake' },
]
const notificationGroups = computed(() => {
  const known = new Set(NOTIFICATION_GROUPS.map(g => g.type))
  return [...NOTIFICATION_GROUPS, { type: 'other', label: 'Прочее', icon: 'lucide:bell' }]
    .map(g => {
      const items = notifications.value.filter(n => g.type === 'other' ? !known.has(n.notification_type || '') : n.notification_type === g.type)
      return { ...g, items, unread: items.filter(n => !n.is_read).length }
    })
    .filter(g => g.items.length)
})
const collapsedNotificationGroups = ref<Set<string>>(new Set())
function toggleNotificationGroup(type: string) {
  const next = new Set(collapsedNotificationGroups.value)
  next.has(type) ? next.delete(type) : next.add(type)
  collapsedNotificationGroups.value = next
}

async function markNotificationRead(n: AppNotification) {
  if (n.is_read) return
  n.is_read = true
  notificationsUnread.value = Math.max(0, notificationsUnread.value - 1)
  try {
    await useApi(`/notifications/${n.id}/read`, { method: 'POST' })
  } catch { /* silent */ }
}

async function openNotification(n: AppNotification) {
  await markNotificationRead(n)
  const link = notificationLink(n)
  if (link) {
    notificationsOpen.value = false
    navigateTo(link)
  }
}

async function markAllNotificationsRead() {
  notifications.value.forEach(n => { n.is_read = true })
  notificationsUnread.value = 0
  try {
    await useApi('/notifications/read-all', { method: 'POST' })
  } catch { /* silent */ }
}

const mailUnread = ref(0)
const onlineCount = ref(0)
const taskCount = ref(0)
// Общий с pages/commercial_proposal/requests.vue — страница обновляет его сразу после смены статуса.
const kpRequestsNew = useState<number>('kp-requests-new-count', () => 0)

async function loadMailUnread() {
  if (!hasFeature('page:mail')) return
  try {
    const data = await useApi<{ count: number }>('/mail/unread-count')
    mailUnread.value = data.count
  } catch { /* silent */ }
}

async function loadOnlineCount() {
  if (!isAdmin.value) return
  try {
    const ids = await useApi<number[]>('/users/online')
    // Себя самого в счётчик "Онлайн" не считаем — не информативно (ты и так знаешь,
    // что ты онлайн).
    onlineCount.value = ids.filter(id => id !== user.value?.id).length
  } catch { /* silent */ }
}

async function loadTaskCount() {
  try {
    const data = await useApi<{ count: number }>('/tasks/my-count')
    taskCount.value = data.count
  } catch { /* silent */ }
}

async function loadKpRequestsNew() {
  try {
    const data = await useApi<{ count: number }>('/suppliers/kp-requests/new-count')
    kpRequestsNew.value = data.count
  } catch { /* silent */ }
}

function loadAllCounts() {
  loadMailUnread()
  loadOnlineCount()
  loadTaskCount()
  loadKpRequestsNew()
  loadNotificationsUnread()
  loadAiUsage()
}

let intervals: ReturnType<typeof setInterval>[] = []

function onVisibilityChange() {
  if (document.visibilityState === 'visible') loadAllCounts()
}

onMounted(() => {
  loadAllCounts()
  intervals = [
    setInterval(loadMailUnread, 60_000),
    setInterval(loadOnlineCount, 30_000),
    setInterval(loadTaskCount, 60_000),
    setInterval(loadKpRequestsNew, 60_000),
    setInterval(loadNotificationsUnread, 60_000),
    setInterval(loadAiUsage, 5 * 60_000),
  ]
  document.addEventListener('visibilitychange', onVisibilityChange)
})

onUnmounted(() => {
  intervals.forEach(clearInterval)
  document.removeEventListener('visibilitychange', onVisibilityChange)
})

// Пересчитываем счётчик задач сразу после перехода на страницу задач/тендеров —
// не ждать минуту до следующего интервала, если пользователь только что что-то изменил.
watch(() => route.path, () => { loadTaskCount(); loadAiUsage() })
// …и сразу после удаления/создания/смены статуса задачи или тендера (тик ставит useApi)
watch(useState<number>('counts:tasks-changed', () => 0), () => loadTaskCount())
// Изменения от коллег/Claude приходят сразу (plugins/realtime.client.ts), а не раз в минуту
useRealtime('task', () => loadTaskCount(), { delay: 1000 })
useRealtime('notification', () => {
  loadNotificationsUnread()
  if (notificationsOpen.value) loadNotifications()
}, { delay: 500 })

const { navigation } = useMainNavigation()

// Цвет счётчика для пункта меню — используется и для развёрнутого badge, и для
// свёрнутого chip, чтобы оба состояния были согласованы.
function countColorFor(href: string): 'error' | 'success' | 'neutral' | undefined {
  if (href === '/mail' && mailUnread.value > 0) return 'error'
  if (href === '/tasks' && taskCount.value > 0) return 'error'
  // Новые запросы КП: на самом подпункте и на родителе «КП» (виден, когда свёрнут).
  if ((href === '/commercial_proposal/requests' || href === '/commercial_proposal') && kpRequestsNew.value > 0) return 'error'
  // Раньше показывался на дочернем пункте "Онлайн" (убран — "Пользователи" больше не
  // раскрывается), теперь счётчик онлайна висит прямо на самом пункте.
  if (href === '/users' && onlineCount.value > 0) return 'success'
  // Лимиты: счётчик виден всегда, красный — когда израсходовано 80% и больше
  if (href === '/limits' && aiUsage.value?.ai_enabled) return aiTendersPercent.value >= 80 ? 'error' : 'neutral'
  return undefined
}

function countLabelFor(href: string): string {
  if (href === '/limits') return aiUsage.value?.ai_enabled ? `${aiUsage.value.tenders_used}/${aiUsage.value.tenders_limit}` : ''
  if (href === '/mail') return mailUnread.value > 99 ? '99+' : String(mailUnread.value)
  if (href === '/tasks') return taskCount.value > 99 ? '99+' : String(taskCount.value)
  if (href === '/commercial_proposal' || href === '/commercial_proposal/requests') return kpRequestsNew.value > 99 ? '99+' : String(kpRequestsNew.value)
  if (href === '/users') return String(onlineCount.value)
  return ''
}

// Развёрнутый сайдбар: badge Nuxt UI, цвета из темы (error/success), но форма — круглая
// заливка, а не дефолтный rounded-md прямоугольник. Дефолтный padding badge (px-1.5 py-1)
// шире, чем выше, поэтому короткие числа получаются овалом, а не кругом — фиксированный
// min-w/h + центрирование без асимметричного padding держат 1-2 цифры настоящим кругом,
// при этом всё ещё растягиваясь (оставаясь скруглённым) для "99+". variant: 'solid' даёт
// сплошной цветной фон (bg). Без тени (shadow) — по запросу пользователя.
const COUNTER_RED = 'bg-red-400'

function badgeFor(href: string): NavigationMenuItem['badge'] {
  const color = countColorFor(href)
  if (!color) return undefined
  return {
    label: countLabelFor(href),
    color,
    // Нейтральный счётчик (лимиты) — мягкий серый, остальные — сплошная заливка
    variant: color === 'neutral' ? 'soft' : 'solid',
    ui: { base: `rounded-full justify-center px-1.5 min-w-[20px] h-[20px] font-bold tabular-nums ${color === 'error' ? COUNTER_RED : ''}` },
  }
}

// Свёрнутый рельс: число всё равно нечитаемо поверх узкой иконки — вместо него штатный
// chip (точка-индикатор) поверх иконки пункта, как в примере "With chip in items" доки.
// ring-0 убирает дефолтную обводку (border) вокруг точки; -top-1 -right-1 (вместо
// дефолтных top-0 right-0) отодвигают её правее и выше — за пределы самой иконки, а не
// вплотную к её углу; h-[10px]/min-w-[10px] (вместо дефолтных 7px) — крупнее, по запросу
// пользователя.
function chipFor(href: string): NavigationMenuItem['chip'] {
  const color = countColorFor(href)
  if (!color || color === 'neutral') return undefined
  return color === 'error' ? { color, ui: { base: `${COUNTER_RED} ring-0 -top-1 -right-1 h-[8px] min-w-[8px]` } } : { color }
}

// Nuxt UI's NavigationMenu only opens the accordion on click when the item has no
// link of its own (clicking a linked parent navigates instead of expanding). When the
// parent's href just duplicates one of its children (e.g. "Товары" -> /products/catalog,
// same as its "Каталог" child), mark it `type: 'trigger'` so clicking always expands —
// the destination stays one click away via the child link, nothing is lost.
// Зависит от collapsed (свёрнутый рельс показывает chip-точку вместо badge с числом —
// см. badgeFor/chipFor), а это состояние доступно только как scoped-slot проп
// UDashboardSidebar, поэтому здесь функция, а не computed — вызывается из шаблона как
// navItemsFor(collapsed).
// Анимация сворачивания/разворачивания (2026-10-08): ширина анимируется классами на root,
// а на время перехода вешается .sidebar-animating — содержимое проявляется fade-ом и без «…»
// у обрезанных подписей (см. main.css). Только по клику, не при загрузке страницы.
const sidebarAnimating = ref(false)
let sidebarAnimTimer: ReturnType<typeof setTimeout> | undefined
function onSidebarCollapseToggle() {
  sidebarAnimating.value = true
  clearTimeout(sidebarAnimTimer)
  sidebarAnimTimer = setTimeout(() => { sidebarAnimating.value = false }, 350)
}
onBeforeUnmount(() => clearTimeout(sidebarAnimTimer))

// Выдвижка на телефоне — полное меню, открывается кнопкой ☰ в шапке (нижнюю панель
// разделов убрали 2026-10-20 по просьбе владельца).
const isMobileNav = useMediaQuery('(max-width: 1023.98px)')
const drawerNavigation = computed(() => navigation.value)

function navItemsFor(collapsed: boolean): NavigationMenuItem[] {
  return drawerNavigation.value.map(item => {
    const hrefDuplicatesChild = item.children?.some(c => c.href === item.href)
    return {
      label: item.name,
      icon: item.icon,
      to: item.href,
      type: hrefDuplicatesChild ? 'trigger' : undefined,
      value: item.href,
      badge: collapsed ? undefined : badgeFor(item.href),
      chip: collapsed ? chipFor(item.href) : undefined,
      // "Товары" всегда раскрыт. Остальные разделы раскрываются только когда мы на одной
      // из их дочерних страниц.
      // на телефоне «Товары» свёрнут — иначе низ выдвижки (уведомления, тема) уходит за экран
      defaultOpen: (item.name === 'Товары' && !isMobileNav.value)
        || item.children?.some(c => route.path === c.href || route.path.startsWith(c.href + '/')),
      children: item.children?.map(child => ({
        label: child.name,
        icon: child.icon,
        to: child.href,
        value: child.href,
        // Дочерний пункт с тем же href, что у родителя («КП» → «Создать»), бейдж
        // родителя не дублирует — счётчик уже висит на родителе.
        badge: collapsed || child.href === item.href ? undefined : badgeFor(child.href),
        chip: collapsed && child.href !== item.href ? chipFor(child.href) : undefined,
      })),
    }
  })
}

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

// Документация живёт на публичном сайте (nuxt-ui-templates saas).
const siteUrl = useRuntimeConfig().public.siteUrl

const userMenuItems = computed<DropdownMenuItem[][]>(() => [[
  { label: 'Профиль', icon: 'lucide:user', to: '/settings' },
  // 2026-10-25: «Тарифный план» убран из меню (по просьбе владельца), тариф — в Настройках
  { label: 'Лимиты', icon: 'lucide:gauge', to: '/settings?tab=plan' },
  { label: 'Настройки', icon: 'lucide:settings', to: '/settings' },
  // Переключатель темы перенесён сюда из сайдбара (2026-10-25); логика та же — useTheme().toggleTheme()
  { label: isDark.value ? 'Светлая тема' : 'Тёмная тема', icon: isDark.value ? 'lucide:sun' : 'lucide:moon', onSelect: () => toggleTheme() },
], [
  { label: 'Помощь', icon: 'lucide:life-buoy', to: `${siteUrl}/contacts`, target: '_blank' },
  { label: 'Документация', icon: 'lucide:book-open', to: `${siteUrl}/docs`, target: '_blank' },
], [
  { label: 'Выйти', icon: 'lucide:log-out', onSelect: () => logout() },
]])
</script>

<template>
  <!-- Сворачивание/разворачивание на десктопе — плавная анимация ширины (2026-10-08; у Nuxt UI
       ширина меняется мгновенно). overflow-hidden — чтобы подписи не вылезали, пока ширина
       догоняет; при перетаскивании края (data-dragging) и «уменьшить движение» в ОС — без анимации. -->
  <UDashboardSidebar
    v-model:open="sidebarOpen"
    :class="{ 'sidebar-animating': sidebarAnimating }"
    collapsible
    @update:collapsed="onSidebarCollapseToggle"
    resizable
    :min-size="14"
    :max-size="24"
    :default-size="16"
    :ui="{ root: 'bg-white dark:bg-gray-900 overflow-hidden motion-safe:transition-[width] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)] data-[dragging=true]:transition-none', footer: 'border-t border-default', header: 'lg:border-b lg:border-default lg:dark:border-gray-800' }"
    :menu="{ content: { onOpenAutoFocus: (e) => e.preventDefault() } }"
  >
    <template #header="{ collapsed }">
      <div v-if="collapsed" class="relative flex items-center justify-center w-full group">
        <AppLogo class="h-[26px] w-[26px] group-hover:opacity-0 transition-opacity" />
        <UDashboardSidebarCollapse icon="lucide:chevron-right" class="absolute inset-0 m-auto opacity-0 group-hover:opacity-100 transition-opacity text-gray-500 dark:text-gray-400" />
      </div>
      <div v-else class="w-full">
        <!-- Мобильная выдвижка: кнопка слева, лого+название по центру. Справа — невидимый
             дубль той же кнопки той же ширины, иначе логотип центрируется в полной ширине
             строки, а слева её "съедает" реальная кнопка — визуально сдвигает центр. -->
        <div class="lg:hidden relative flex items-center w-full">
          <UDashboardSidebarCollapse icon="lucide:chevron-left" class="text-gray-500 dark:text-gray-400 flex-shrink-0" />
          <div class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-2 min-w-0">
            <AppLogo class="h-[26px] w-[26px] shrink-0" />
            <span class="text-base font-bold truncate text-[#17233D] dark:text-white font-[Geist]">Zakupki <span class="text-primary-500 dark:text-primary-500">HUB</span></span>
          </div>
          <UDashboardSidebarCollapse icon="lucide:chevron-left" class="invisible pointer-events-none flex-shrink-0 ml-auto" aria-hidden="true" tabindex="-1" />
        </div>

        <!-- Десктопный рельсовый сайдбар: логотип слева, кнопка сворачивания справа. -->
        <div class="hidden lg:flex items-center justify-between gap-2 min-w-0 w-full">
          <div class="flex items-center gap-2 min-w-0">
            <AppLogo class="h-[26px] w-[26px] shrink-0" />
            <span class="text-base font-bold truncate text-[#17233D] dark:text-white font-[Geist]">Zakupki <span class="text-primary-500 dark:text-primary-500">HUB</span></span>
          </div>
          <UDashboardSidebarCollapse icon="lucide:chevron-left" class="text-gray-500 dark:text-gray-400 flex-shrink-0" />
        </div>
      </div>
    </template>

    <template #default="{ collapsed }">
      <!-- sidebar-nav-scope (см. assets/css/main.css): переопределяет --ui-bg-elevated
           только внутри этого блока и только в светлой теме, чтобы фон активного пункта
           (Nuxt UI рисует его через before:bg-elevated при active=true) стал #F1F5F9, а
           неактивные пункты остались прозрачными — раньше это переопределялось
           безусловно через :ui.link, из-за чего фон "прилипал" и к неактивным пунктам. -->
      <div class="sidebar-nav-scope">
        <UNavigationMenu
          :collapsed="collapsed"
          :items="navItemsFor(collapsed)"
          orientation="vertical"
          tooltip
          popover
          :ui="{ linkLeadingIcon: 'size-6 lg:size-5', link: 'text-base py-2.5 lg:text-sm lg:py-1.5' }"
        />
      </div>

      <!-- Кнопка уведомлений — прижата к низу (mt-auto). Тема — в меню профиля. -->
      <UTooltip :text="collapsed ? 'Уведомления' : ''" :content="{ side: 'right' }">
        <button
          type="button"
          class="flex items-center gap-3 w-full rounded-lg px-2.5 py-2.5 lg:py-2 mt-auto text-base lg:text-sm font-medium transition-colors text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
          :class="collapsed ? 'justify-center' : ''"
          title="Уведомления"
          :aria-label="notificationsUnread ? `Уведомления, непрочитанных: ${notificationsUnread}` : 'Уведомления'"
          @click="notificationsOpen = true"
        >
          <span class="relative flex-shrink-0">
            <Icon name="lucide:bell" class="size-6 lg:size-5" />
            <span
              v-if="notificationsUnread && collapsed"
              class="absolute -top-1 -right-1 h-[8px] min-w-[8px] rounded-full bg-red-400"
            />
          </span>
          <span v-if="!collapsed" class="truncate">Уведомления</span>
          <span
            v-if="notificationsUnread && !collapsed"
            class="ml-auto inline-flex items-center justify-center rounded-full px-1.5 min-w-[20px] h-[20px] text-xs font-bold text-white bg-red-400"
          >
            {{ notificationsUnread > 99 ? '99+' : notificationsUnread }}
          </span>
        </button>
      </UTooltip>

      <!-- Переключатель темы с 2026-10-25 — в меню профиля (userMenuItems) -->
    </template>

    <template #footer="{ collapsed }">
      <!-- footer — flex-row; без flex-1 обёртка сжимается по контенту кнопки (сама узкая) —
           тогда ни justify-center (аватар по центру в свёрнутом виде), ни ml-auto на
           trailing-иконке (шеврон у правого края в развёрнутом) не имеют реального
           пространства, чтобы сработать. flex-1 растягивает обёртку на всю ширину рельса
           всегда; -mx-2 и justify-center — только в свёрнутом виде (компенсация padding
           footer'а под тесный рельс ~64px, где аватар иначе обрезался по бокам). -->
      <!-- Колонка: пользователь сверху, AI-ассистент под ним. -->
      <div class="flex-1 flex flex-col gap-2 min-w-0">
      <div :class="['flex', collapsed ? '-mx-2 justify-center' : '']">
      <!-- Меню профиля: белый фон, цвета пунктов как у навигации сайдбара выше (2026-10-09) -->
      <UDropdownMenu :items="userMenuItems" :content="{ align: 'center', collisionPadding: 12 }" :ui="{ content: [collapsed ? 'w-56' : 'w-(--reka-dropdown-menu-trigger-width)', 'bg-white dark:bg-gray-900'].join(' '), item: 'font-medium text-[#6A7282] dark:text-[#9CA3AF] data-highlighted:text-[#333333] dark:data-highlighted:text-[#F3F4F6]', itemLeadingIcon: 'text-[#6A7282] dark:text-[#9CA3AF] group-data-highlighted:text-[#333333] dark:group-data-highlighted:text-[#F3F4F6]' }">
        <UButton
          color="neutral"
          variant="ghost"
          block
          :square="collapsed"
          :class="['data-[state=open]:bg-elevated', collapsed ? '!p-1' : '']"
          :ui="{ trailingIcon: 'text-dimmed ml-auto' }"
          :trailing-icon="collapsed ? undefined : 'i-lucide-chevrons-up-down'"
        >
          <template #leading>
            <!-- Тариф «Команда» — цветная рамка (.avatar-plan-ring в main.css) -->
            <span :class="hasTeamPlan ? 'avatar-plan-ring' : 'contents'" :title="hasTeamPlan ? 'Тариф «Команда»' : undefined">
              <span :class="hasTeamPlan ? 'avatar-plan-ring__gap' : 'contents'">
                <img v-if="user?.avatar_url" :src="user.avatar_url" alt="Avatar" width="24" height="24" class="size-6 rounded-full object-cover shrink-0" />
                <span
                  v-else
                  class="size-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 text-white"
                  :style="{ background: user?.avatar_color ? `linear-gradient(135deg, ${user.avatar_color}, ${user.avatar_color}dd)` : 'linear-gradient(135deg, var(--color-primary-500), var(--color-primary-600))' }"
                >{{ user?.name ? getInitials(user.name) : '?' }}</span>
              </span>
            </span>
          </template>
          <span v-if="!collapsed" class="truncate">{{ user?.name }}</span>
        </UButton>
      </UDropdownMenu>
      </div>

      <!-- AI-ассистент: та же панель (ChatFab.vue), что открывается кнопкой в шапке
           (см. HeaderActions.vue) — здесь просто ещё одна точка входа. Перенесена в футер
           под имя пользователя по запросу пользователя 2026-09-27 (до этого — над "Обзор").
           Скрывается через NUXT_PUBLIC_SIDEBAR_HIDE=ai (crm.zakupkihub.ru, 2026-10-18). -->
      <UTooltip v-if="!hideAiButton" :text="collapsed ? 'AI-ассистент' : ''" :content="{ side: 'right' }">
        <button
          type="button"
          class="flex items-center justify-center gap-3 w-full rounded-xl px-2.5 py-2 text-sm font-medium transition-colors bg-primary-50 hover:bg-primary-100 border border-primary-200 text-primary-500 dark:bg-primary-400/15 dark:hover:bg-primary-400/25 dark:border-transparent dark:text-primary-300 shadow-xs"
          title="AI-ассистент"
          @click="openAiChat"
        >
          <IconsChatAi class="w-5 h-5 flex-shrink-0" />
          <span v-if="!collapsed" class="truncate">AI-ассистент</span>
        </button>
      </UTooltip>
      </div>
    </template>
  </UDashboardSidebar>

  <!-- Нижняя панель разделов на телефоне (< lg); «Ещё» открывает эту же выдвижку -->

  <!-- Слайдовер уведомлений. Пустое состояние — бэкенд/данные под уведомления ещё не
       реализованы, панель пока только UI-заготовка. -->
  <USlideover v-model:open="notificationsOpen" title="Уведомления">
    <template #actions>
      <UButton
        v-if="notificationsUnread"
        size="xs"
        color="neutral"
        variant="ghost"
        icon="lucide:check-check"
        @click="markAllNotificationsRead"
      >
        Прочитать все
      </UButton>
    </template>
    <template #body>
      <div v-if="notificationsLoading && !notifications.length" class="flex justify-center py-16">
        <Icon name="lucide:loader-2" class="w-6 h-6 text-gray-400 animate-spin" />
      </div>
      <div v-else-if="!notifications.length" class="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <Icon name="lucide:bell-off" class="w-10 h-10 text-gray-300 dark:text-gray-600" />
        <p class="text-sm text-gray-500 dark:text-gray-400">Пока нет уведомлений</p>
      </div>
      <div v-else class="flex flex-col gap-3">
        <section v-for="g in notificationGroups" :key="g.type">
          <button
            type="button"
            class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800"
            :aria-expanded="!collapsedNotificationGroups.has(g.type)"
            :aria-controls="`notif-group-${g.type}`"
            @click="toggleNotificationGroup(g.type)"
          >
            <Icon
              name="lucide:chevron-right"
              class="w-4 h-4 flex-shrink-0 text-gray-400 transition-transform"
              :class="collapsedNotificationGroups.has(g.type) ? '' : 'rotate-90'"
            />
            <Icon :name="g.icon" class="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
            <span class="flex-1 truncate text-sm font-semibold text-gray-900 dark:text-white">{{ g.label }}</span>
            <span v-if="g.unread" class="rounded-full bg-blue-500 px-1.5 text-xs font-medium leading-5 text-white">{{ g.unread }}</span>
            <span class="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{{ g.items.length }}</span>
          </button>
          <ul v-show="!collapsedNotificationGroups.has(g.type)" :id="`notif-group-${g.type}`" class="mt-1 flex flex-col gap-1">
            <li v-for="n in g.items" :key="n.id">
              <button
                type="button"
                class="flex w-full items-start gap-3 rounded-lg p-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                :class="n.is_read ? '' : 'bg-blue-50/60 dark:bg-blue-950/30'"
                @click="openNotification(n)"
              >
                <Icon :name="notificationIcon(n)" class="w-5 h-5 mt-0.5 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                <span class="min-w-0 flex-1">
                  <span class="flex items-baseline justify-between gap-2">
                    <span class="text-sm text-gray-900 dark:text-white" :class="n.is_read ? 'font-normal' : 'font-semibold'">{{ n.title }}</span>
                    <span class="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">{{ formatRelativeShort(n.created_at) }}</span>
                  </span>
                  <span v-if="n.message" class="mt-0.5 block text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{{ n.message }}</span>
                </span>
                <span v-if="!n.is_read" class="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-blue-500" aria-label="Не прочитано" />
              </button>
            </li>
          </ul>
        </section>
      </div>
    </template>
  </USlideover>
</template>
